<template>
  <Transition name="scan-feedback">
    <div v-if="isVisible && feedbackConfig" class="scan-feedback" :class="feedbackConfig.className" data-testid="scan-feedback">
      <div class="scan-feedback-content">
        <i :class="feedbackConfig.icon"></i>
        <div class="scan-feedback-text">
          <strong data-testid="scan-feedback-title">{{ feedbackConfig.title }}</strong>
          <span v-if="feedbackMessage" data-testid="scan-feedback-message">{{ feedbackMessage }}</span>
        </div>
      </div>
      <Button
        icon="pi pi-times"
        severity="secondary"
        text
        rounded
        class="scan-feedback-close"
        data-testid="scan-feedback-close"
        :aria-label="t('Fechar feedback')"
        @click="dismiss"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface ScanResult {
  action?: string;
  message?: string;
  auto_completed_step?: { name?: string };
  [key: string]: unknown;
}

interface FeedbackConfig {
  className: string;
  icon: string;
  title: string;
}

interface Props {
  result?: ScanResult | null;
}

const props = withDefaults(defineProps<Props>(), {
  result: null
});

const emit = defineEmits<{
  'dismiss': [];
}>();

const { t } = useI18n();

const isVisible = ref<boolean>(false);
let dismissTimer: number | null = null;

const clearDismissTimer = (): void => {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
};

const feedbackConfig = computed<FeedbackConfig | null>(() => {
  const action = props.result?.action || '';
  const configMap: Record<string, FeedbackConfig> = {
    start: {
      className: 'feedback-success',
      icon: 'pi pi-check-circle',
      title: t('Iniciado')
    },
    complete: {
      className: 'feedback-success',
      icon: 'pi pi-check-circle',
      title: t('Concluído')
    },
    out_of_order_start: {
      className: 'feedback-warning',
      icon: 'pi pi-exclamation-triangle',
      title: t('Iniciado (fora de ordem)')
    },
    blocked_no_laudo: {
      className: 'feedback-danger',
      icon: 'pi pi-times-circle',
      title: t('Bloqueado')
    },
    error: {
      className: 'feedback-danger',
      icon: 'pi pi-times-circle',
      title: t('Erro')
    },
    info_all_done: {
      className: 'feedback-info',
      icon: 'pi pi-info-circle',
      title: t('Todos os passos concluídos')
    },
    out_of_order_warning: {
      className: 'feedback-warning',
      icon: 'pi pi-exclamation-triangle',
      title: t('Fora de ordem')
    }
  };

  return configMap[action] || null;
});

const feedbackMessage = computed<string>(() => {
  const action = props.result?.action || '';

  if (action === 'start' && props.result?.auto_completed_step?.name) {
    return `${props.result.auto_completed_step.name} ${t('foi concluído automaticamente.')}`;
  }

  if (!props.result?.message) {
    return '';
  }

  if (['blocked_no_laudo', 'error', 'out_of_order_warning'].includes(action)) {
    return t(props.result.message);
  }

  return props.result.message === feedbackConfig.value?.title ? '' : t(props.result.message);
});

const dismiss = (): void => {
  clearDismissTimer();
  isVisible.value = false;
  emit('dismiss');
};

watch(() => props.result, (newResult: ScanResult | null | undefined) => {
  clearDismissTimer();
  isVisible.value = Boolean(newResult);

  if (newResult) {
    dismissTimer = window.setTimeout(() => {
      dismiss();
    }, 3000);
  }
}, { immediate: true });

onBeforeUnmount(() => {
  clearDismissTimer();
});
</script>

<style scoped>
.scan-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  color: #fff;
}

.scan-feedback-content {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.scan-feedback-content > i {
  font-size: 1.1rem;
}

.scan-feedback-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.scan-feedback-text strong,
.scan-feedback-text span {
  white-space: normal;
}

.feedback-success {
  background: linear-gradient(135deg, #15803d, #22c55e);
}

.feedback-warning {
  background: linear-gradient(135deg, #b45309, #f59e0b);
}

.feedback-danger {
  background: linear-gradient(135deg, #b91c1c, #ef4444);
}

.feedback-info {
  background: linear-gradient(135deg, #1d4ed8, #3b82f6);
}

.scan-feedback-close {
  flex: 0 0 auto;
}

.scan-feedback-close:deep(.p-button) {
  color: #fff;
}

.scan-feedback-enter-active,
.scan-feedback-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.scan-feedback-enter-from,
.scan-feedback-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
